/**
 * R4 (Punkt 2) — OAuth-Consent-Seite für den MCP-Connector (z. B. claude.ai).
 *
 * Der /api/oauth/authorize-Endpoint schickt den Browser hierher, wenn der
 * Request ohne gewähltes mcp_token ankommt (so startet claude.ai den Flow).
 * Die Seite liegt im Auth-Rumpf: ohne Login erscheint erst die Login-Maske,
 * die URL (inkl. OAuth-Query) bleibt dabei erhalten. Hier wählt der Nutzer das
 * MCP-Token (= Ordner-Scope + Rolle), das der Connector leihen darf, und kehrt
 * per Vollnavigation zu /api/oauth/authorize zurück — der Server prüft dann
 * serverseitig manage_acl auf dem Scope-Ordner (der Consent-Gate bleibt dort).
 *
 * „Ablehnen“ navigiert bewusst NUR auf „/“ — nie auf eine aus den Query-
 * Parametern gebaute Adresse (Open-Redirect-Vermeidung; die redirect_uri wird
 * ausschließlich serverseitig validiert und benutzt).
 */

import { useCallback, useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";

import "./ryfe.scss";

import type { McpToken } from "../api";

interface TokenRow extends McpToken {
  workspaceName: string;
}

export const RyfeOAuthConsent = () => {
  const { workspaces } = useRyfe();
  const [tokens, setTokens] = useState<TokenRow[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const clientName = params.get("client_name") || "";
  // Ein Consent ohne die OAuth-Pflichtparameter kann nichts autorisieren —
  // dann ist die Seite direkt (ohne Flow) aufgerufen worden.
  const flowOk =
    !!params.get("client_id") &&
    !!params.get("redirect_uri") &&
    !!params.get("code_challenge");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Je Workspace isoliert laden: ein einzelner fehlschlagender Abruf (z. B.
    // 403 in einem fremden Workspace) darf die Token der übrigen Workspaces
    // nicht verschlucken. Fehler zeigen wir nur, wenn ALLE Abrufe scheitern.
    const all: TokenRow[] = [];
    let failures = 0;
    let lastError = "";
    for (const ws of workspaces) {
      try {
        const res = await ryfeApi.listMcpTokens(ws.id);
        for (const t of res.tokens) {
          all.push({ ...t, workspaceName: ws.name });
        }
      } catch (err) {
        failures += 1;
        lastError = (err as Error).message;
      }
    }
    setTokens(all);
    const firstActive = all.find((t) => t.status === "active");
    if (firstActive) {
      setSelected(firstActive.id);
    }
    if (failures > 0 && all.length === 0 && workspaces.length > 0) {
      setError(lastError || "Token-Liste konnte nicht geladen werden.");
    }
    setLoading(false);
  }, [workspaces]);

  useEffect(() => {
    load();
  }, [load]);

  const allow = () => {
    if (!selected) {
      return;
    }
    // Vollnavigation zurück zum Authorize-Endpoint: die ursprünglichen
    // OAuth-Parameter reisen unverändert mit, plus das gewählte Token.
    const qs = new URLSearchParams(search);
    qs.delete("mcp_token_id");
    qs.set("mcp_token_id", selected);
    window.location.assign(`/api/oauth/authorize?${qs.toString()}`);
  };

  return (
    <div className="ryfe-page ryfe-consent">
      <h1 className="ryfe-page__title">Zugriff erlauben?</h1>
      <p className="ryfe-muted">
        {clientName ? `„${clientName}“` : "Eine externe Anwendung"} möchte über
        den MCP-Endpunkt auf deine Dateien zugreifen. Wähle das Zugriffstoken,
        dessen Ordner-Scope und Rolle die Anwendung erhalten soll — mehr als
        dieses Token erlaubt, kann sie nie sehen.
      </p>

      {!flowOk && (
        <div className="ryfe-auth__error" role="alert">
          Diese Seite wurde ohne laufende OAuth-Anfrage aufgerufen. Starte die
          Verbindung in der externen Anwendung (z.&nbsp;B. Claude) neu.
        </div>
      )}

      {error && (
        <div className="ryfe-auth__error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="ryfe-muted">Loading…</div>
      ) : tokens.length === 0 ? (
        <div className="ryfe-panel">
          <p className="ryfe-muted">
            Es gibt noch kein MCP-Token. Erstelle zuerst eines am gewünschten
            Ordner in „Meine Dateien“ (Kontextmenü → MCP-Token) und starte die
            Verbindung danach erneut.
          </p>
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={() => navigate("/files")}
          >
            Zu „Meine Dateien“
          </button>
        </div>
      ) : (
        <div className="ryfe-consent__list" role="radiogroup">
          {tokens.map((t) => (
            <label
              key={t.id}
              className={`ryfe-consent__row${
                t.status !== "active" ? " ryfe-consent__row--disabled" : ""
              }`}
            >
              <input
                type="radio"
                name="mcp-token"
                value={t.id}
                checked={selected === t.id}
                disabled={t.status !== "active"}
                onChange={() => setSelected(t.id)}
              />
              <span className="ryfe-consent__name">{t.name}</span>
              <span className="ryfe-muted">
                {t.workspaceName} · Ordner: {t.folder_name || "—"} · Rolle:{" "}
                {t.role || "—"}
                {t.status !== "active" ? " · abgelaufen" : ""}
              </span>
            </label>
          ))}
        </div>
      )}

      <div className="ryfe-modal__actions">
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => navigate("/")}
        >
          Ablehnen
        </button>
        <button
          className="ryfe-btn ryfe-btn--primary"
          onClick={allow}
          disabled={!flowOk || !selected}
          data-testid="oauth-allow"
        >
          Erlauben
        </button>
      </div>
    </div>
  );
};
